// Theme input component for starting a new game
import { useState, useMemo } from 'react';
import { CHARACTER_CLASSES, CharacterClass } from '../../types';
import './NewGame.css';

const STORY_THEMES = [
  'Gothic vampire court with political intrigue',
  'Space pirate crew on a stolen ship',
  'Desert treasure hunt through ancient ruins',
  'Haunted Victorian mansion',
  'Post-apocalyptic survival in a ruined city',
  'Dragon\'s lair expedition',
  'Medieval political intrigue at court',
  'Lost underwater city discovery',
  'Arctic expedition to find a lost civilization',
  'Wild west outlaw adventure',
  'Jungle temple expedition',
  'Cyberpunk hacker heist',
  'Fairy tale reimagining',
  'Celtic myth adventure',
  'Zombie apocalypse survival',
  'Steampunk airship voyage',
  'Ninja clan war',
  'Wizard academy mystery',
  'Pirate treasure map',
  'Haunted carnival',
];

const CHARACTER_NAMES = [
  'Aldric Stormborn',
  'Seraphina Nightwhisper',
  'Thorne Ironheart',
  'Lyra Shadowmend',
  'Grimshaw the Bold',
  'Elara Dawnstrider',
  'Fenwick Ashborne',
  'Isolde Ravencrest',
  'Kael Frostbane',
  'Miriel Starweaver',
  'Orin Blackthorn',
  'Vesper Moonfall',
  'Zara Emberveil',
  'Dorian Wyrmwood',
  'Brynn Copperfield',
  'Sable Duskhollow',
  'Rowan Ashvale',
  'Nyx Silvertongue',
  'Cassian Dreadmoor',
  'Wren Holloway',
  // Sci-Fi
  'Jax Voidrunner',
  'Nova Kepler',
  'Zephyr Caine',
  'Orion Vex',
  'Reva Solstice',
  'Kira Nebulon',
  'Dax Helion',
  'Seren Arcturus',
  'Mako Zenith',
  'Lyris Quantum',
  'Cade Parallax',
  'Voss Andromeda',
  'Ari Pulsewave',
  'Talon Redshift',
  'Echo Stellaris',
  // Normal
  'Marcus Cole',
  'Elena Vasquez',
  'James Hartwell',
  'Sophia Chen',
  'Oliver Blake',
  'Amara Okafor',
  'Liam Donnelly',
  'Hana Kimura',
  'Nathan Cross',
  'Ivy Callahan',
  'Sam Reeves',
  'Clara Moreno',
  'Ethan Shaw',
  'Maya Patel',
  'Jack Thorne',
];

interface NewGameProps {
  onStartGame: (theme: string, characterName: string, characterClass: CharacterClass) => void;
  loading?: boolean;
}

export function NewGame({ onStartGame, loading }: NewGameProps) {
  const [theme, setTheme] = useState('');
  const [characterName, setCharacterName] = useState('');
  const [characterClass, setCharacterClass] = useState<CharacterClass>('warrior');
  const [step, setStep] = useState<'theme' | 'character'>('theme');

  const randomTheme = useMemo(() => {
    const shuffled = [...STORY_THEMES].sort(() => Math.random() - 0.5);
    return shuffled[0];
  }, []);

  const handleThemeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (theme.trim()) {
      setStep('character');
    }
  };

  const handleCharacterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (characterName.trim()) {
      onStartGame(theme, characterName, characterClass);
    }
  };

  const handleRandomTheme = () => {
    const randomIndex = Math.floor(Math.random() * STORY_THEMES.length);
    setTheme(STORY_THEMES[randomIndex]);
  };

  const handleRandomName = () => {
    const randomIndex = Math.floor(Math.random() * CHARACTER_NAMES.length);
    setCharacterName(CHARACTER_NAMES[randomIndex]);
  };

  if (step === 'theme') {
    return (
      <div className="new-game fade-in">
        <div className="new-game-card">
          <h2>Begin Your Adventure</h2>
          <p className="new-game-intro">
            Enter a theme for your story. What world shall we explore together?
          </p>
          
          <form onSubmit={handleThemeSubmit} className="new-game-form">
            <div className="form-group">
              <label htmlFor="theme">Story Theme</label>
              <div className="input-with-button">
                <input
                  id="theme"
                  type="text"
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  placeholder={`eg. ${randomTheme}`}
                  autoFocus
                />
                <button
                  type="button"
                  className="btn btn-secondary dice-btn"
                  onClick={handleRandomTheme}
                  title="Roll for random theme"
                >
                  🎲
                </button>
              </div>
              <p className="form-hint">
                Be as creative as you like - the AI will craft your tale around it.
              </p>
            </div>

            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={!theme.trim() || loading}
            >
              {loading ? (
                <span className="cursor-blink">Creating...</span>
              ) : (
                'Continue'
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="new-game fade-in">
      <div className="new-game-card">
        <h2>Choose Your Hero</h2>
        <p className="new-game-intro">
          Who are you, {characterName || 'adventurer'}?
        </p>

        <div className="class-grid">
          {(Object.entries(CHARACTER_CLASSES) as [CharacterClass, typeof CHARACTER_CLASSES[CharacterClass]][]).map(
            ([key, cls]) => (
              <button
                key={key}
                className={`class-card ${characterClass === key ? 'selected' : ''}`}
                onClick={() => setCharacterClass(key)}
              >
                <span className="class-icon">{cls.icon}</span>
                <span className="class-name">{cls.name}</span>
                <span className="class-desc">{cls.description}</span>
                <div className="class-stats">
                  <span>STR {cls.stats.str}</span>
                  <span>DEX {cls.stats.dex}</span>
                  <span>CON {cls.stats.con}</span>
                  <span>INT {cls.stats.int}</span>
                  <span>WIS {cls.stats.wis}</span>
                  <span>CHA {cls.stats.cha}</span>
                </div>
              </button>
            )
          )}
        </div>

        <form onSubmit={handleCharacterSubmit} className="new-game-form">
          <div className="form-group">
            <label htmlFor="characterName">Character Name</label>
            <div className="input-with-button">
              <input
                id="characterName"
                type="text"
                value={characterName}
                onChange={(e) => setCharacterName(e.target.value)}
                placeholder="Enter your name..."
                autoFocus
              />
              <button
                type="button"
                className="btn btn-secondary dice-btn"
                onClick={handleRandomName}
                title="Roll for random name"
              >
                🎲
              </button>
            </div>
          </div>

          <div className="button-row">
            <button 
              type="button" 
              className="btn"
              onClick={() => setStep('theme')}
              disabled={loading}
            >
              Back
            </button>
            <button 
              type="submit" 
              className="btn btn-primary"
              disabled={!characterName.trim() || loading}
            >
              {loading ? (
                <span className="cursor-blink">Starting...</span>
              ) : (
                'Begin Adventure'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
